// "Hidden. Undo" + "Not into these?" with the event's types (web/profile-ui.js showHideToast). Goes away after
// TOAST_IDLE untouched, never while the pointer or focus is on it. Mounted once at the app root.
import { useEffect, useRef, useState } from "react";
import { T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import { useProfileView } from "../../state/profile";
import { tagEmoji } from "../../state/feed";
import { dismissHideToast, removeHideToast, useHideToast } from "./hideToastState";
import styles from "./profile.module.css";

const TOAST_IDLE = 8000;

export function HideToast() {
  const { session, leaving } = useHideToast();
  const view = useProfileView(); // chips redraw when a type's level changes
  const el = useRef<HTMLDivElement>(null);
  const [touched, setTouched] = useState(0); // a touch or tap starts the wait again
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!session || leaving) return;
    const arm = () => {
      clearTimeout(timer.current);
      // Checked when time is up: chips re-render and move focus around, so "engaged" is read then.
      timer.current = setTimeout(() => {
        const node = el.current;
        if (node && (node.matches(":hover") || node.contains(document.activeElement))) arm(); else dismissHideToast();
      }, TOAST_IDLE);
    };
    arm();
    return () => clearTimeout(timer.current);
  }, [session, leaving, touched]);

  if (!session) return null;
  const tags = session.tags;
  const allHidden = tags.length > 1 && tags.every((t) => view.hiddenTags.has(t));
  return (
    <div ref={el} id="hide-toast" className={`${styles["toast"]}${leaving ? ` ${styles["leaving"]}` : ""}`} role="status" aria-live="polite"
      onAnimationEnd={() => { if (leaving) removeHideToast(); }}
      onPointerDown={() => setTouched((n) => n + 1)} onPointerLeave={() => setTouched((n) => n + 1)}>
      <div className={styles["toast-top"]}>
        <b>{T("toast.hidden")}</b>
        <span className={styles["toast-title"]}>{session.event.title}</span>
        <button type="button" id="hide-undo" className={styles["link-btn"]} onClick={() => { session.undo(); dismissHideToast(); }}>{T("toast.undo")}</button>
        <button type="button" className={`${styles["close"]} ${styles["small"]}`} aria-label={T("close")} onClick={dismissHideToast}><Icon name="x" /></button>
      </div>
      {tags.length > 0 && <div className={`${styles["muted"]} ${styles["small"]} ${styles["toast-q"]}`}>{T("toast.question")}</div>}
      {tags.length > 0 && (
        <div className={styles["tags"]}>
          {tags.map((t) => {
            const emoji = tagEmoji(t);
            return (
              <button key={t} type="button" data-tag={t} aria-pressed={view.hiddenTags.has(t)}
                className={`${styles["chip"]}${view.hiddenTags.has(t) ? ` ${styles["on"]}` : ""}`} onClick={() => session.toggleTag(t)}>{emoji ? `${emoji} ${t}` : t}</button>
            );
          })}
          {tags.length > 1 && (
            <button type="button" id="hide-all-tags" disabled={allHidden} className={`${styles["chip"]}${allHidden ? ` ${styles["on"]}` : ""}`}
              onClick={() => session.hideAllTags()}>{T("toast.allOfThese")}</button>
          )}
        </div>
      )}
    </div>
  );
}
